import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";

interface Props {
  targetType: "venue" | "publication" | "live" | "ad_creative";
  targetId: string;
  targetLabel: string;
  onClose: () => void;
}

const LABELS: Record<string, string> = { venue: "ce lieu", publication: "cette publication", live: "ce Live", ad_creative: "cette publicité" };
const GENDERS = [{ value: "all", label: "Tout le monde" }, { value: "homme", label: "Hommes" }, { value: "femme", label: "Femmes" }];

export default function SponsorModal({ targetType, targetId, targetLabel, onClose }: Props) {
  const [radiusKm, setRadiusKm] = useState(15);
  const [minAge, setMinAge] = useState(18);
  const [maxAge, setMaxAge] = useState(65);
  const [gender, setGender] = useState("all");
  const [budget, setBudget] = useState(20);
  const [days, setDays] = useState(3);
  const [reach, setReach] = useState<number | null>(null);
  const [loadingReach, setLoadingReach] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoadingReach(true);
    debounceRef.current = setTimeout(() => {
      api.post("/sponsorships/estimate", { target_type: targetType, target_id: targetId, radius_km: radiusKm, min_age: minAge, max_age: maxAge, gender })
        .then(data => setReach(data?.estimated_reach ?? null))
        .catch(() => setReach(null))
        .finally(() => setLoadingReach(false));
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [targetType, targetId, radiusKm, minAge, maxAge, gender]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const successUrl = `${window.location.origin}/sponsor/success`;
      const cancelUrl = window.location.href;
      const data = await api.post("/sponsorships", {
        target_type: targetType, target_id: targetId,
        radius_km: radiusKm, min_age: minAge, max_age: maxAge, gender,
        budget_total: budget, duration_days: days,
        success_url: successUrl, cancel_url: cancelUrl,
      });
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        setError(data?.error || "Erreur lors de la création de la campagne");
        setSubmitting(false);
      }
    } catch {
      setError("Erreur de connexion");
      setSubmitting(false);
    }
  };

  const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: "#666", display: "block", marginBottom: 8, marginTop: 18 };
  const sliderRow = (val: number, setVal: (n: number) => void, min: number, max: number, step: number, suffix: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <input type="range" min={min} max={max} step={step} value={val} onChange={e => setVal(Number(e.target.value))} style={{ flex: 1 }} />
      <span style={{ fontSize: 13, fontWeight: 700, minWidth: 64, textAlign: "right" }}>{val}{suffix}</span>
    </div>
  );

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 18, fontWeight: 800 }}>Booster {LABELS[targetType]}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>{targetLabel}</p>

        <label style={label}>Rayon de diffusion</label>
        {sliderRow(radiusKm, setRadiusKm, 1, 100, 1, " km")}

        <label style={label}>Âge minimum</label>
        {sliderRow(minAge, v => setMinAge(Math.min(v, maxAge)), 16, 80, 1, " ans")}
        <label style={label}>Âge maximum</label>
        {sliderRow(maxAge, v => setMaxAge(Math.max(v, minAge)), 16, 80, 1, " ans")}

        <label style={label}>Genre</label>
        <div style={{ display: "flex", gap: 8 }}>
          {GENDERS.map(g => (
            <button key={g.value} onClick={() => setGender(g.value)} style={{
              flex: 1, padding: "9px 0", borderRadius: 20, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
              border: gender === g.value ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
              backgroundColor: gender === g.value ? "#0a0a0a" : "white", color: gender === g.value ? "white" : "#666",
            }}>{g.label}</button>
          ))}
        </div>

        <div style={{ backgroundColor: "#F5F5F5", borderRadius: 14, padding: 16, marginTop: 20, textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: 0.5, textTransform: "uppercase" }}>Portée estimée</div>
          <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>
            {loadingReach ? "…" : reach != null ? `~${reach.toLocaleString("fr-FR")} personnes` : "—"}
          </div>
        </div>

        <label style={label}>Budget total</label>
        {sliderRow(budget, setBudget, 5, 500, 5, " €")}

        <label style={label}>Durée</label>
        {sliderRow(days, setDays, 1, 30, 1, " j")}

        {!!error && <div style={{ color: "#c0392b", fontSize: 13, marginTop: 14 }}>{error}</div>}

        <button onClick={submit} disabled={submitting} style={{ width: "100%", padding: "15px", borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 15, fontWeight: 700, cursor: submitting ? "default" : "pointer", marginTop: 20, opacity: submitting ? 0.7 : 1 }}>
          {submitting ? "..." : `Payer ${budget} € et lancer la campagne`}
        </button>
        <p style={{ fontSize: 11, color: "#999", textAlign: "center", marginTop: 10 }}>
          Paiement sécurisé via Stripe. Commission OLTODO de 10 % HT incluse.
        </p>
      </div>
    </div>
  ,
    document.body
  );
}
